import React, { useEffect, useState } from "react";
import { api, fmtCurrency, AU_STATES } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import {
  TrendUp, UsersFour, Buildings, Kanban, ShieldCheck,
  WarningCircle, PaperPlaneTilt, Receipt, ArrowsClockwise, Clock, MapPin,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STAGE_META = {
  "deal":               { label: "Deal",              color: "bg-blue-500",       text: "text-blue-700" },
  "discovery":          { label: "Discovery",         color: "bg-indigo-500",     text: "text-indigo-700" },
  "scope-in-progress":  { label: "Scope in Progress", color: "bg-purple-500",     text: "text-purple-700" },
  "sow-proposal-ready": { label: "SOW Ready",         color: "bg-violet-500",     text: "text-violet-700" },
  "closed-won":         { label: "Closed Won",        color: "bg-emerald-500",    text: "text-emerald-700" },
  "config-in-progress": { label: "Config In Progress",color: "bg-cyan-500",       text: "text-cyan-700" },
  "deployment":         { label: "Deployment",        color: "bg-orange-500",     text: "text-orange-700" },
  "delivered":          { label: "Delivered",         color: "bg-teal-500",       text: "text-teal-700" },
  "rework":             { label: "Rework",            color: "bg-red-500",        text: "text-red-700" },
  "closed-lost":        { label: "Closed Lost",       color: "bg-slate-400",      text: "text-slate-600" },
};

// Mirrors backend LOST_CATEGORIES + Pipeline.jsx (kept in sync manually — small enough).
const LOST_LABEL = {
  budget:       "Budget constraints",
  board:        "Board / management declined",
  scope:        "Scope changed",
  competitor:   "Chose a competitor",
  timing:       "Timing — deferred",
  no_response:  "No response from client",
  other:        "Other",
  unspecified:  "Unspecified",
};

const KPI = ({ label, value, hint, icon: Icon, accent = "text-blue-600", testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white rounded-lg p-5">
    <div className="flex items-start justify-between">
      <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <Icon size={18} weight="duotone" className={accent} />
    </div>
    <div className="mt-3 font-display font-bold text-3xl tracking-tight text-[#0F1E38]">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hbLoading, setHbLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard/v2");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runHeartbeat = async () => {
    setHbLoading(true);
    try {
      await api.post("/integration/psdoc/heartbeat/run");
      await load();
      toast.success("PSDoc integration checked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Heartbeat check failed");
    } finally {
      setHbLoading(false);
    }
  };

  const [retryLoading, setRetryLoading] = useState(false);
  const retryAllPending = async () => {
    if (!window.confirm("Retry all pending PSDoc pushes?\n\nRe-pushes stuck discovery jobs, polls PSDoc for latest notes on in-flight discoveries, and re-pushes any pending 'awaiting client reply' status.")) return;
    setRetryLoading(true);
    try {
      const r = await api.post("/integration/psdoc/retry-all-pending");
      const d = r.data || {};
      const disc = d.discoveries_repushed?.length || 0;
      const polled = d.notes_polled?.length || 0;
      const status = d.status_repushed?.length || 0;
      const errs = d.errors?.length || 0;
      toast.success(`Retry complete · ${disc} discoveries · ${polled} polls · ${status} statuses · ${errs} errors`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetryLoading(false);
    }
  };

  if (loading || !data) {
    return <div className="p-8 text-slate-400" data-testid="dashboard-loading">Loading Command Center…</div>;
  }

  const maxFunnelValue = Math.max(...data.funnel.map(f => f.value), 1);

  return (
    <div className="space-y-6" data-testid="dashboard-v2">
      <PageHeader
        overline="Operations / Live"
        title="Command Center"
        subtitle="Leads → Discovery → PSDoc → Won → Deployment — every deal, one screen."
        actions={
          <Button variant="outline" size="sm" onClick={load} data-testid="dashboard-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        }
      />

      {/* Top KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI
          label="Leads"
          value={data.totals.leads}
          hint={`${data.totals.clients} converted to clients`}
          icon={UsersFour} accent="text-blue-600"
          testid="kpi-leads"
        />
        <KPI
          label="Pipeline Value"
          value={fmtCurrency(data.totals.pipeline_value)}
          hint={`${data.totals.projects} projects tracked`}
          icon={TrendUp} accent="text-violet-600"
          testid="kpi-pipeline"
        />
        <KPI
          label="Won + In Deployment"
          value={fmtCurrency(data.totals.won_value)}
          hint="Signed and delivering"
          icon={ShieldCheck} accent="text-emerald-600"
          testid="kpi-won"
        />
        <KPI
          label="Aged Deals (>21d)"
          value={data.aged_deals.length}
          hint="Stuck in early stages"
          icon={WarningCircle} accent={data.aged_deals.length > 0 ? "text-red-600" : "text-slate-500"}
          testid="kpi-aged"
        />
      </div>

      {/* Deal-flow funnel */}
      <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="funnel-widget">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Deal-flow funnel</div>
            <div className="text-lg font-semibold text-[#0F1E38] mt-0.5">Every stage in the new tri-app pipeline</div>
          </div>
          <Link to="/pipeline" className="text-xs text-blue-600 hover:underline">Open Pipeline →</Link>
        </div>
        <div className="space-y-2">
          {data.funnel.map((f) => {
            const meta = STAGE_META[f.stage] || { label: f.stage, color: "bg-slate-400", text: "text-slate-600" };
            const pct = maxFunnelValue > 0 ? (f.value / maxFunnelValue) * 100 : 0;
            return (
              <div key={f.stage} className="flex items-center gap-3">
                <div className={`w-40 text-xs font-medium ${meta.text}`}>{meta.label}</div>
                <div className="flex-1 h-8 bg-slate-100 rounded overflow-hidden relative">
                  <div
                    className={`h-full ${meta.color} opacity-70 transition-all`}
                    style={{ width: `${pct}%`, minWidth: f.count > 0 ? "4px" : 0 }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                    <span className="font-medium text-slate-700">{f.count} {f.count === 1 ? "deal" : "deals"}</span>
                    <span className="font-mono text-slate-600">{fmtCurrency(f.value)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* "Why we lost" — insight into deals that fell through */}
      {(data.lost_total?.count > 0) && (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="lost-breakdown-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Why we lost</div>
              <div className="text-lg font-semibold text-[#0F1E38] mt-0.5">
                {data.lost_total.count} lost {data.lost_total.count === 1 ? "deal" : "deals"} · {fmtCurrency(data.lost_total.value)} of pipeline
              </div>
            </div>
            <Link to="/pipeline" className="text-xs text-blue-600 hover:underline">Open Pipeline →</Link>
          </div>
          <div className="space-y-2">
            {data.lost_breakdown.map((row) => {
              const pct = data.lost_total.count > 0 ? (row.count / data.lost_total.count) * 100 : 0;
              return (
                <details key={row.category} className="group rounded border border-slate-200 open:bg-slate-50" data-testid={`lost-row-${row.category}`}>
                  <summary className="flex items-center gap-3 px-3 py-2 cursor-pointer list-none">
                    <div className="w-44 text-xs font-medium text-red-700 uppercase tracking-wider">{LOST_LABEL[row.category] || row.category}</div>
                    <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden relative">
                      <div className="h-full bg-red-500/60" style={{ width: `${pct}%`, minWidth: row.count > 0 ? "4px" : 0 }} />
                      <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                        <span className="font-medium text-slate-700">{row.count} {row.count === 1 ? "deal" : "deals"}</span>
                        <span className="font-mono text-slate-600">{fmtCurrency(row.value)}</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 group-open:hidden">details ↓</span>
                    <span className="text-[10px] font-mono text-slate-400 hidden group-open:inline">hide ↑</span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 space-y-1.5">
                    {row.examples.map((ex, i) => (
                      <div key={i} className="text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[#0F1E38]">{ex.name}</span>
                          <span className="font-mono text-slate-500">{fmtCurrency(ex.value)}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono">{ex.client}</div>
                        {ex.reason && <div className="text-[11px] text-slate-600 mt-1 whitespace-pre-wrap italic">"{ex.reason}"</div>}
                      </div>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* PSDoc integration health */}
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="psdoc-health">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <PaperPlaneTilt size={18} className="text-blue-600" />
              <div className="text-lg font-semibold text-[#0F1E38]">PSDoc Integration</div>
            </div>
            <div className="flex items-center gap-2">
              <UnifiedStatusBadge status={data.psdoc.heartbeat?.status || (data.psdoc.configured ? "awaiting_first_use" : "not_configured")} />
              <Button size="sm" variant="outline" onClick={runHeartbeat} disabled={hbLoading} data-testid="psdoc-run-check" className="h-7 text-xs">
                <ArrowsClockwise size={12} className="mr-1" /> {hbLoading ? "Checking…" : "Run check"}
              </Button>
              <Button size="sm" onClick={retryAllPending} disabled={retryLoading} data-testid="psdoc-retry-all" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white" title="Re-push every stuck discovery, poll for missed notes, and mirror any pending status updates to PSDoc">
                <ArrowsClockwise size={12} className="mr-1" /> {retryLoading ? "Retrying…" : "Retry all pending"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold text-slate-800">{data.psdoc.recent_events_total}</div>
              <div className="text-[11px] uppercase text-slate-500">Recent events</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600">{data.psdoc.recent_ok}</div>
              <div className="text-[11px] uppercase text-slate-500">Successful</div>
            </div>
            <div>
              <div className={`text-2xl font-bold ${data.psdoc.recent_errors > 0 ? "text-red-600" : "text-slate-400"}`}>{data.psdoc.recent_errors}</div>
              <div className="text-[11px] uppercase text-slate-500">Failures</div>
            </div>
          </div>

          <div className="mt-4 text-xs space-y-1 border-t border-slate-100 pt-3">
            <FreshnessRow label="Last outbound OK" iso={data.psdoc.last_ok_outbound_at} coldLabel="not used yet" />
            <FreshnessRow label="Last inbound webhook" iso={data.psdoc.last_inbound_at} coldLabel="not received yet" />
            {data.psdoc.heartbeat && (
              <FreshnessRow
                label="Last heartbeat"
                iso={data.psdoc.heartbeat.ran_at}
                suffix={data.psdoc.heartbeat.outbound?.latency_ms != null ? `${data.psdoc.heartbeat.outbound.latency_ms}ms` : null}
              />
            )}
          </div>

          <HeartbeatSparkline />
        </div>

        {/* Upcoming milestones */}
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="upcoming-milestones">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-emerald-600" />
              <div className="text-lg font-semibold text-[#0F1E38]">Milestones (14 days)</div>
            </div>
            <Link to="/milestones" className="text-xs text-blue-600 hover:underline">All →</Link>
          </div>
          {data.upcoming_milestones.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No milestones due in the next 14 days.</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.upcoming_milestones.map((m) => (
                <div key={m.milestone_id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.project_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono">{fmtCurrency(m.amount)}</div>
                    <div className={`text-xs ${m.days_until < 0 ? "text-red-600 font-medium" : m.days_until <= 3 ? "text-amber-600 font-medium" : "text-slate-500"}`}>
                      {m.days_until < 0 ? `${Math.abs(m.days_until)}d overdue` : m.days_until === 0 ? "today" : `in ${m.days_until}d`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Two-column: Leads by state + Aged deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leads by AU state */}
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="leads-by-state">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-slate-600" />
              <div className="text-lg font-semibold text-[#0F1E38]">Leads by state</div>
            </div>
            <Link to="/leads" className="text-xs text-blue-600 hover:underline">Open Leads →</Link>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {AU_STATES.map((s) => {
              const count = data.leads_by_state[s.code] || 0;
              return (
                <div key={s.code} className={`text-center rounded-lg py-2 px-1 border ${count > 0 ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="text-lg font-bold text-slate-800">{count}</div>
                  <div className="text-[10px] font-mono uppercase text-slate-500">{s.code}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Aged deals */}
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="aged-deals">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-red-500" />
              <div className="text-lg font-semibold text-[#0F1E38]">Aged deals</div>
            </div>
            <Link to="/pipeline" className="text-xs text-blue-600 hover:underline">Pipeline →</Link>
          </div>
          {data.aged_deals.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No deals stuck — everything is moving.</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data.aged_deals.map((d) => (
                <div key={d.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{d.name}</div>
                    <div className="text-xs">
                      <span className={`text-[10px] font-mono uppercase px-1 py-0.5 rounded ${STAGE_META[d.stage]?.text || "text-slate-500"}`}>
                        {STAGE_META[d.stage]?.label || d.stage}
                      </span>
                      <span className="text-slate-500 ml-2">{d.days_open}d open</span>
                    </div>
                  </div>
                  <div className="text-sm font-mono text-slate-700">{fmtCurrency(d.value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UnifiedStatusBadge({ status }) {
  const cfg = {
    healthy:            { label: "Healthy",             cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    degraded:           { label: "Degraded",            cls: "bg-red-100 text-red-700 border-red-200" },
    awaiting_first_use: { label: "Awaiting First Use",  cls: "bg-blue-100 text-blue-700 border-blue-200" },
    not_configured:    { label: "Not Configured",       cls: "bg-slate-100 text-slate-600 border-slate-300" },
  };
  const { label, cls } = cfg[status] || cfg.not_configured;
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
}

function FreshnessRow({ label, iso, coldLabel = "never", suffix = null }) {
  if (!iso) {
    return (
      <div className="flex items-center justify-between text-slate-500">
        <span>{label}</span>
        <span className="italic text-slate-400">{coldLabel}</span>
      </div>
    );
  }
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diffH = (now - t) / 3600000;
  const tone = diffH > 24 ? "text-red-600" : diffH > 6 ? "text-amber-600" : "text-emerald-600";
  const human = diffH < 1/60 ? "just now"
              : diffH < 1 ? `${Math.max(1, Math.round(diffH * 60))} min ago`
              : diffH < 24 ? `${Math.round(diffH)}h ago`
              : `${Math.round(diffH / 24)}d ago`;
  return (
    <div className="flex items-center justify-between text-slate-600">
      <span>{label}</span>
      <span className={`font-mono ${tone}`}>
        {human}{suffix && <span className="text-slate-400 ml-2">({suffix})</span>}
      </span>
    </div>
  );
}


function HeartbeatSparkline() {
  const [history, setHistory] = React.useState(null);

  React.useEffect(() => {
    api.get("/integration/psdoc/heartbeat/status").then((r) => setHistory(r.data.history || [])).catch(() => setHistory([]));
  }, []);

  if (history === null) return null;
  if (history.length < 2) {
    return (
      <div className="mt-4 text-xs text-slate-400 border-t border-slate-100 pt-3">
        7-day latency trend appears once we have more heartbeat samples ({history.length} so far)
      </div>
    );
  }

  const points = history.filter((h) => h.outbound?.latency_ms != null);
  const latencies = points.map((h) => h.outbound.latency_ms);
  const min = Math.min(...latencies);
  const max = Math.max(...latencies);
  const avg = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
  const failed = history.filter((h) => !h.outbound?.ok && !h.outbound?.skipped).length;

  // Build SVG path
  const W = 340, H = 60, PAD = 4;
  const range = Math.max(1, max - min);
  const step = (W - PAD * 2) / Math.max(1, points.length - 1);
  const path = points
    .map((h, i) => {
      const x = PAD + i * step;
      const y = PAD + (H - PAD * 2) * (1 - (h.outbound.latency_ms - min) / range);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  // First & last timestamps (labels)
  const first = new Date(history[0].ran_at);
  const last = new Date(history[history.length - 1].ran_at);
  const fmt = (d) => d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="heartbeat-sparkline">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase text-slate-500 font-medium">7-day latency</div>
        <div className="text-[10px] font-mono text-slate-500 flex gap-3">
          <span>min <b className="text-emerald-600">{min}ms</b></span>
          <span>avg <b className="text-blue-600">{avg}ms</b></span>
          <span>max <b className={max > 3000 ? "text-red-600" : "text-slate-700"}>{max}ms</b></span>
          {failed > 0 && <span className="text-red-600">✗ {failed}</span>}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
        <defs>
          <linearGradient id="hbGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* area fill under the line */}
        <path
          d={`${path} L ${(PAD + (points.length - 1) * step).toFixed(1)} ${H - PAD} L ${PAD} ${H - PAD} Z`}
          fill="url(#hbGrad)"
        />
        {/* the trend line */}
        <path d={path} stroke="#3B82F6" strokeWidth="1.5" fill="none" />
        {/* red dots for failed heartbeats */}
        {history.map((h, i) => {
          if (h.outbound?.ok || h.outbound?.skipped) return null;
          const x = PAD + i * step;
          return <circle key={i} cx={x} cy={H - PAD} r="2.2" fill="#EF4444" />;
        })}
      </svg>
      <div className="flex justify-between text-[10px] font-mono text-slate-400 mt-0.5">
        <span>{fmt(first)}</span>
        <span>{fmt(last)}</span>
      </div>
    </div>
  );
}

